import { FC, useContext, useMemo, useState } from 'react';

import { Preloader } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Bar,
  BarChart,
  Cell,
  Brush,
} from 'recharts';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { IWorkShiftChartProps } from './interfaces';
import { Tooltip as CustomTooltip } from './Tooltip';
import s from './WorkShiftChart.module.scss';

export const WorkShiftChart: FC<IWorkShiftChartProps> = ({
  workShifts,
  loading,
  openWorkShiftDetails,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const [focusBar, setFocusBar] = useState<number | null>(null);

  const normalizedData = useMemo(() => {
    return workShifts?.map((item) => {
      return {
        id: item.id,
        startDate: item.starts,
        finishDate: item.finish,
        income: item.proceeds,
        operator: item.workerFIO,
      };
    });
  }, [workShifts]);

  return (
    <div className={s.root}>
      <div className={s.title}>{t('report.workShift.proceeds.period')}</div>
      {loading ? (
        <div className={s.empty}>
          <Preloader loading={true} />
        </div>
      ) : normalizedData === undefined ? (
        <div className={s['empty']}>{t('report.placeholder.emptyDates')}</div>
      ) : normalizedData.length === 0 ? (
        <div className={s['empty']}>{t('report.placeholder')}</div>
      ) : (
        <ResponsiveContainer className={s.data}>
          <BarChart
            width={500}
            height={400}
            data={normalizedData.sort((a, b) => a.id - b.id)}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
            onMouseMove={(state) => {
              if (state.isTooltipActive) {
                setFocusBar(state.activeTooltipIndex ?? null);
              } else {
                setFocusBar(null);
              }
            }}
          >
            <CartesianGrid strokeDasharray="0" vertical={false} />
            <XAxis
              axisLine={false}
              dataKey="startDate"
              tick={{ fill: 'var(--text-primary)', strokeWidth: 0 }}
              tickFormatter={(value: string) => {
                const date = value === 'auto' ? 0 : value;
                return dayjs(date).format('DD.MM');
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => {
                return `${value.toLocaleString()}${currency.sign}`;
              }}
              tick={{ fill: 'var(--text-primary)', strokeWidth: 0 }}
              tickSize={6}
            />
            <Tooltip
              cursor={false}
              content={({ payload }) => (
                <CustomTooltip payload={payload?.[0]?.payload} />
              )}
            />
            <Brush
              dataKey="startDate"
              height={32}
              radius={8}
              stroke="var(--text-disable)"
              fill="var(--bg-item-primary)"
              travellerWidth={32}
              tickFormatter={(value) => dayjs(value).format('DD.MM')}
              traveller={(props) => (
                <svg
                  {...props}
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="24" height="24" fill="var(--bg-item-primary)" />
                  <rect
                    x="8"
                    y="7.5"
                    width="8"
                    height="1"
                    fill="var(--text-primary)"
                  />
                  <rect
                    x="8"
                    y="11.5"
                    width="8"
                    height="1"
                    fill="var(--text-primary)"
                  />
                  <rect
                    x="8"
                    y="15.5"
                    width="8"
                    height="1"
                    fill="var(--text-primary)"
                  />
                </svg>
              )}
            />

            <Bar
              fill="var(--bg-blue-10)"
              barSize={400}
              strokeWidth={2}
              dataKey="income"
              radius={[8, 8, 0, 0]}
              onDoubleClick={(workShift) => {
                if (!permissions?.has('work_shift_browse')) return;

                openWorkShiftDetails(workShift.id);
              }}
            >
              {normalizedData.map((_, index) => (
                <Cell
                  key={index}
                  fill={
                    focusBar === index ? 'var(--bg-blue)' : 'var(--bg-blue-10)'
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
